import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiCalendar, FiInfo } from 'react-icons/fi';
import { useClient } from 'jsonapi-react';
import moment from 'moment';
import { statusBadgeText } from '../../utils/BadgeEssay';
import { EssayAboutTips } from '../essay-about-tips';
import { Loader } from '@/components/custom/loader';
import toast from 'react-hot-toast';
import { DashboardContent } from '@/layouts/dashboard';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { CourseAbout, CourseAboutBody, CourseAboutDescription, CourseAboutHeader, CourseAboutSubtitle, CourseAboutTitle, CourseBanner, CourseBannerContainer, CourseBannerContent, CourseBannerCTA, CourseBannerTitle } from '@/components/custom/course';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { EssayTips, EssayTipsIcon } from '@/components/custom/essay';

export default function EssayAboutDefault({ essayId, uri }) {
  const client = useClient();
  const [essay, setEssay] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const getEssay = async () => {
    setLoading(true);
    const { data, error } = await client.fetch(`/essays/${essayId}`);
    if (error) {
      if (error.title === 'Essay not available') {
        toast.error('Redação não disponível');
        window.location.href = '/redacao';
      } else {
        toast.error('Erro ao buscar redação');
      }
    } else {
      setEssay(data);
    }
    setLoading(false);
  };

  useEffect(() => {
    getEssay();
  }, []);

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: '/redacao',
        name: 'Redação',
        isActive: false
      },
      {
        route: uri,
        name: essay?.title ? essay?.title : essay?.topic,
        isActive: true
      }
    ]
  };

  const statusBadgeColor = {
    accepting_proposal: 'attention',
    close_for_submissions: 'neutral',
    processing: 'warning',
    unreviewed: 'success',
    reviewed: 'success',
    annulled: 'danger',
    annulled_by_teacher: 'danger ',
    minimum_length_not_satisfied: 'danger',
    failed_to_process: 'danger',
    reviewing: 'warning',
    not_sent: 'neutral',
    ocr_review: 'warning',
    sending_by_teacher: 'warning'
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      {loading ? (
        <Loader />
      ) : (
        <CourseAbout>
          <CourseBanner className="min-h-[224px]">
            <CourseBannerContent>
              <CourseBannerContainer>
                <Badge
                  size="xs"
                  variant={statusBadgeColor?.[essay?.status]}
                  title={statusBadgeText[essay?.status]}
                >
                  {statusBadgeText[essay?.status]}
                </Badge>

                <CourseBannerTitle title={essay?.title ? essay?.title : essay?.topic}>{essay?.title ? essay?.title : essay?.topic}</CourseBannerTitle>

                {((essay && essay?.status === 'accepting_proposal') || essay?.status === 'minimum_length_not_satisfied' || (essay?.status === 'ocr_review' && essay?.['essay-student']?.['submission_order'] < 3)) && (
                  <CourseBannerCTA>
                    <Button
                      variant="outline"
                      wide
                      asChild
                    >
                      <Link to={`/redacao/enviar/${essay?.id}`}>Enviar a redação</Link>
                    </Button>
                  </CourseBannerCTA>
                )}

                {essay?.status === 'annulled' && (
                  <CourseBannerCTA>
                    <Button
                      wide
                      asChild
                    >
                      <Link to={`/redacao/consulta/${essay?.id}/${essay?.['essay-student']?.id}`}>Acessar redação</Link>
                    </Button>
                  </CourseBannerCTA>
                )}
              </CourseBannerContainer>
            </CourseBannerContent>
          </CourseBanner>

          {essay?.status === 'processing' && (
            <Alert className="self-center">
              <FiInfo />
              <AlertTitle>Sua redação está sendo corrigida. O resultado estará disponível aqui em breve.</AlertTitle>
            </Alert>
          )}

          <div className="flex flex-wrap items-center gap-6">
            <Badge
              square
              variant="bright"
              className="flex h-20 grow items-center justify-center rounded-xl"
            >
              <span className="text-sm font-light text-primary-dark">
                <FiCalendar
                  size={16}
                  className="align-sub text-primary"
                />
                Entregue a redação até <br />
                <strong className="text-lg">{moment(essay?.['delivery-date']).format('DD/MM/YYYY')}</strong>
              </span>
            </Badge>

            <Badge
              square
              variant="bright"
              className="flex h-20 grow items-center justify-center rounded-xl"
            >
              <span className="text-sm font-light text-primary-dark">
                Essa redação permite <br />
                {/* Aguardar definição da regra de quantidade de envios */}
                <strong className="text-lg">{essay?.['allow-multiple-submissions'] ? '2 envios' : '1 envio'} para correção</strong>
              </span>
            </Badge>
          </div>

          <CourseAboutHeader>
            <CourseAboutTitle>Enunciado</CourseAboutTitle>

            <CourseAboutDescription className="whitespace-pre-wrap">{essay?.statement}</CourseAboutDescription>

            <CourseAboutDescription>
              Tema: {essay?.topic}
              <br />
              Título: <strong>{essay?.title}</strong>
            </CourseAboutDescription>
          </CourseAboutHeader>

          <CourseAboutBody>
            <CourseAboutTitle>Textos motivadores</CourseAboutTitle>

            {essay?.['essay-motivator-texts']?.map((item, index) => {
              return (
                <article>
                  <CourseAboutSubtitle>Texto {index + 1}</CourseAboutSubtitle>
                  <CourseAboutDescription dangerouslySetInnerHTML={{ __html: item.body }} />
                  {/* <p className="course-about__description">Disponível em: https://brasilescola.uol.com.br/portugues/preconceito-linguistico.htm. Acesso em: 19 ago. 2022 (adaptado).</p> */}
                </article>
              );
            })}
          </CourseAboutBody>
        </CourseAbout>
      )}

      <EssayTips onClick={() => setOpenModal(true)}>
        <EssayTipsIcon>
          <FiInfo />
        </EssayTipsIcon>
        Como enviar minha redação?
      </EssayTips>

      <Dialog
        open={openModal}
        onOpenChange={() => {
          setOpenModal(false);
        }}
      >
        <DialogContent>
          <EssayAboutTips essayId={essayId} />
        </DialogContent>
      </Dialog>
    </DashboardContent>
  );
}
